import { styled } from '@ori-ui/mui';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';

import { useAppContext } from '../../contexts';
import { useTranslationByKey } from '../../helpers/useTranslationByKeys';
import { TranslationKey } from '../../models';
import { Loading } from '../Loading/Loading';
import { PopupHeader } from '../PopupHeader/PopupHeader';
import { NotifyTypeEnum } from './helpers/NotifyTypeEnum';
import { NotifyForm } from './NotifyForm/NotifyForm';
import { NotifyType } from './NotifyType/NotifyType';
import { Thanks } from './Thanks/Thanks';

const defaultData: UserDataFromAPI = {
    email: null,
    telephoneNumber: null,
};

interface UserDataFromAPI {
    email: string | null;
    telephoneNumber: string | null;
}

interface NotifyCumulusProps {
    productCode: string;
}

const Root = styled('div')(({ theme }) => ({
    height: 300,
    overflow: 'hidden',
    [theme.breakpoints.up(768)]: {
        boxSizing: 'border-box',
        width: 350,
        padding: '0 10px',
    },
}));

export const NotifyCumulus: React.FC<NotifyCumulusProps> = ({ productCode }) => {
    const { notifyMeSubscriptionConfiguration, onlineBaseUrl } = useAppContext();
    const t = useTranslationByKey();
    const [currentType, changeType] = useState(NotifyTypeEnum.email);
    const [inputData, changeInputData] = useState('');
    const [isReserved, changeReservationState] = useState(false);
    const [userDataFromAPI, setUserData] = useState(defaultData);
    const fetchData = async (): Promise<void> => {
        const { data }: AxiosResponse<UserDataFromAPI> = await axios.get(
            new URL('/system/ajax/stockNotification/userData', onlineBaseUrl ?? window.location.origin).toString(),
        );

        setUserData(data);
        if (data.email && currentType === NotifyTypeEnum.email) {
            changeInputData(data.email);
        } else if (data.telephoneNumber && currentType === NotifyTypeEnum.sms) {
            changeInputData(data.telephoneNumber);
        }
    };

    useEffect(
        () => {
            fetchData();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Old ignore rule
        [],
    );

    const handleOnChangeData = useCallback(
        (event: React.FormEvent<HTMLInputElement>): void => {
            if (!notifyMeSubscriptionConfiguration.IsEditionEnabled) {
                return;
            }

            const trimmedValue = event.currentTarget.value.trim();

            changeInputData(trimmedValue);
        },
        [notifyMeSubscriptionConfiguration.IsEditionEnabled],
    );

    const handleOnChangeType = useCallback(
        (event: React.FormEvent<HTMLInputElement>): void => {
            const { value } = event.currentTarget;

            changeType(value as NotifyTypeEnum);
            if (userDataFromAPI.email && value === NotifyTypeEnum.email) {
                changeInputData(userDataFromAPI.email);
            } else if (userDataFromAPI.telephoneNumber && value === NotifyTypeEnum.sms) {
                changeInputData(userDataFromAPI.telephoneNumber);
            }
        },
        [userDataFromAPI.email, userDataFromAPI.telephoneNumber],
    );

    const handleOnSubmit = useCallback((): void => {
        changeReservationState(true);
    }, []);

    if (!inputData) {
        return (
            <Root>
                <Loading />
            </Root>
        );
    }

    return (
        <Root>
            {isReserved ? (
                <Thanks contactToUser={inputData} />
            ) : (
                <>
                    <PopupHeader title={t(TranslationKey.notifyMe)} />
                    <NotifyType currentType={currentType} handleOnChange={handleOnChangeType} />
                    <NotifyForm
                        currentType={currentType}
                        handleOnChange={handleOnChangeData}
                        handleOnSubmit={handleOnSubmit}
                        inputData={inputData}
                        productCode={productCode}
                    />
                </>
            )}
        </Root>
    );
};
